import React, { useEffect, useState } from 'react';
import { StickyNote, Send, Trash2 } from 'lucide-react';
import { getToken } from '@/services/authService';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Internal note board — visible only when the visitor is authenticated
// (store / admin / editor / despacho). Public shoppers never see it.
// Stores use it to jot quick notes about a product (e.g. "quedan 3 en tienda",
// "revisar batería", "cliente pidió reponer color rojo").
export default function ProductNotes({ productId }) {
  const [notes, setNotes] = useState(null);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const token = getToken();

  const load = async () => {
    if (!token) { setNotes([]); return; }
    const res = await fetch(`${API}/products/${productId}/notes`, { headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) setNotes(await res.json());
    else setNotes([]);
  };
  useEffect(() => { load().catch(() => setNotes([])); /* eslint-disable-next-line */ }, [productId]);

  if (!token) return null; // public visitors never see this

  const add = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try {
      const res = await fetch(`${API}/products/${productId}/notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ product_id: productId, text: text.trim() }),
      });
      if (res.ok) { setText(''); await load(); }
    } finally { setBusy(false); }
  };
  const del = async (id) => {
    await fetch(`${API}/products/${productId}/notes/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    await load();
  };

  return (
    <section className="mt-8 bg-yellow-50 border border-yellow-200 rounded-2xl p-4" data-testid="product-notes">
      <h3 className="text-sm font-bold text-[#1B2A4A] flex items-center gap-2 mb-2">
        <StickyNote size={16} /> Notas internas de la tienda
        <span className="text-[10px] font-normal text-yellow-700 bg-yellow-200/60 rounded-full px-2">Solo visible para el equipo BIGTIME</span>
      </h3>
      <form onSubmit={add} className="flex gap-2 mb-3">
        <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Nota rápida sobre este producto…" className="flex-1 px-3 py-2 text-sm rounded-lg border border-yellow-200" data-testid="product-note-input" />
        <button type="submit" onClick={add} disabled={busy || !text.trim()} className="bg-[#1B2A4A] text-yellow-300 rounded-full px-4 disabled:opacity-40 flex items-center gap-1" data-testid="product-note-add">
          <Send size={13} /> Agregar
        </button>
      </form>
      <div className="space-y-2" data-testid="product-notes-list">
        {notes === null && <p className="text-xs text-slate-500">Cargando…</p>}
        {notes?.length === 0 && <p className="text-xs text-slate-500">Aún no hay notas. Sé el primero en anotar algo del producto.</p>}
        {notes?.map((n) => (
          <div key={n.id} className="bg-white border border-slate-200 rounded-lg p-2.5 text-sm flex items-start gap-2" data-testid={`product-note-${n.id}`}>
            <div className="flex-1 min-w-0">
              <p className="text-slate-700 whitespace-pre-wrap">{n.text}</p>
              <p className="text-[10px] text-slate-400 mt-0.5">{n.author_name || 'Anónimo'} · {n.author_role} · {new Date(n.created_at).toLocaleString('es-VE')}</p>
            </div>
            <button onClick={() => del(n.id)} className="text-red-400 hover:text-red-600 p-1" title="Eliminar" data-testid={`product-note-del-${n.id}`}>
              <Trash2 size={13} />
            </button>
          </div>
        ))}
      </div>
    </section>
  );
}
